// @ts-check
// Página del shell: monta la navegación superior de nivel superior (TSK-0090,
// MOD-FRONTEND-CORE, docs/03-frontend.md §4.3 "pages": "Compone features y
// componentes para una URL/vista. Gestiona montaje y desmontaje. No
// implementa cliente HTTP ni reglas reutilizables").
//
// Es el ÚNICO módulo que monta esta barra, en las NUEVE páginas que la llevan:
// index, admin, perfil, progress, pseudocode, python y las tres guías.
// Todas anclan en `#shell-nav-root` y cargan este archivo.
//
// Hasta el 2026-08-05 había tres módulos casi gemelos: este,
// `pseudocode-shell-nav.js` y `python-shell-nav.js` (85 líneas cada uno, con
// 14 distintas entre sí). Los dos especializados no aportaban comportamiento
// propio: fijaban su sección en vez de deducirla del `pathname`, algo que
// `detectarSeccionActiva` YA sabía hacer para `/pseudocode.html` y
// `/python.html` desde que existe el mapa de `utils/bloques.js`. Existían solo
// porque su HTML anclaba en `#pseudocode-nav-root`/`#python-nav-root`, así que
// se unificó el anclaje en los tres HTML y se borraron los dos módulos y sus
// dos CSS (el mismo margen negativo con otro nombre de clase). Si aparece una
// página nueva con barra: anclaje `#shell-nav-root`, este script, y una línea
// en `detectarSeccionActiva` si además debe resaltar algún enlace.
//
// El componente TSK-0089
// (`crearNavegacionSuperior`, `../components/top-nav.js`) es PURO -- no
// detecta la sección activa por sí mismo ni orquesta sesión, ambas se
// reciben por parámetro -- así que esta página es quien decide esos dos
// valores y quien consulta `state/session-store.js` (nunca `fetch` directo
// desde aquí, criterio de aceptación de la ficha).
//
// DECISIÓN registrada sobre el landmark duplicado (criterio de aceptación,
// CLAUDE.md "no decidas silenciosamente"): `crearNavegacionSuperior` ya
// construye su propio `<nav aria-label="Editores">` (fijo en TSK-0089, fuera
// de las rutas permitidas de esta tarea -- no se puede tocar ese archivo ni
// cambiar su aria-label). El shell reservaba en `frontend/index.html` un
// `<nav aria-label="Navegación principal">` vacío (TSK-0009). Montar el
// `<nav>` del componente DENTRO de ese otro `<nav>` produciría landmarks de
// navegación anidados (dos roles "navigation" en el árbol de accesibilidad,
// uno de ellos sin contenido propio, ambos accesibles por teclado/lector de
// pantalla -- confuso y redundante). Se opta por la alternativa que ofrece
// la ficha: el placeholder del shell DEJA de ser un `<nav>` -- ahora es un
// `<div id="shell-nav-root">` sin rol de landmark, ver `frontend/index.html`
// -- y el `<nav>` que el componente ya trae se inserta dentro de ese `div`,
// quedando como el ÚNICO landmark de navegación del documento resultante
// (`aria-label="Editores"`, heredado del componente sin modificarlo).
import { crearNavegacionSuperior } from '../components/top-nav.js';
import { aplicarEstiloGuardado } from './nav-acciones.js';
import { construirMenuSesionNav, redirigirALogin } from './nav-session.js';
import { cargarSesionActual } from '../state/session-store.js';
import { migrarDesdeCuenta } from '../state/visual-style.js';
import { SECCION_POR_PAGINA } from '../utils/bloques.js';
import './shell-nav.css';

/** @typedef {import('../components/top-nav.js').SeccionEditor} SeccionEditor */

/**
 * Id del punto de anclaje reservado, idéntico en las nueve páginas con barra
 * (ver la cabecera de este archivo).
 */
export const ID_ANCLAJE = 'shell-nav-root';

/**
 * Detecta la sección activa a partir del `pathname` del documento actual
 * (mismo contrato de rutas fijado por TSK-0089: `index.html` -> 'diagramas',
 * `pseudocode.html` -> 'pseudocodigo', `python.html` -> 'python'; TSK-0141,
 * Ola 7, añade `progress.html` -> 'progreso'; la unificación de las tres
 * barras añade `guia-pseudocodigo.html` -> 'pseudocodigo'). Una raíz de sitio
 * (pathname vacío o terminado en '/', que cualquier servidor estático resuelve
 * sirviendo `index.html`) también cuenta como 'diagramas'. Cualquier otra
 * ruta devuelve `null` (ningún enlace se marca activo) -- decisión
 * conservadora: mejor ningún `aria-current` que uno incorrecto en una página
 * futura todavía no contemplada por este contrato.
 * @param {string} pathname
 * @returns {SeccionEditor | null}
 */
export function detectarSeccionActiva(pathname) {
  const normalizado = pathname.trim();

  // La raíz del sitio la sirve cualquier servidor estático como `index.html`,
  // así que cuenta como el bloque cuya página ES `index.html`. Se resuelve por
  // el mapa, no con 'diagramas' escrito a mano, para que siga siendo cierto si
  // algún día cambia qué bloque vive en la portada.
  const seccionPortada = SECCION_POR_PAGINA.get('index.html') ?? null;
  if (normalizado === '' || normalizado.endsWith('/')) {
    return seccionPortada;
  }

  // Mapa INVERSO derivado de `utils/bloques.js` (ver `SECCION_POR_PAGINA`), en
  // vez de una cadena de `endsWith` escrita a mano por bloque: era la cuarta
  // copia del mismo mapa y la única en sentido contrario.
  for (const [pagina, seccion] of SECCION_POR_PAGINA) {
    if (normalizado.endsWith(`/${pagina}`)) return seccion;
  }

  // Secciones que NO son bloques del itinerario (no tienen editor ni
  // ejercicios): se quedan aquí porque no forman parte del mapa compartido.
  if (normalizado.endsWith('/progress.html')) return 'progreso';
  if (normalizado.endsWith('/admin.html')) return 'admin';

  // La guía de aprendizaje tampoco es un bloque del itinerario (no tiene
  // editor ni ejercicios propios), pero SÍ pertenece al de pseudocódigo. Se
  // mapea a mano para conservar exactamente lo que veía el alumno antes de
  // unificar las tres barras (2026-08-05): hasta entonces esta página montaba
  // `pseudocode-shell-nav.js`, que fijaba `seccionActiva: 'pseudocodigo'`.
  // Dejarla caer en `null` habría sido legítimo, pero es un cambio VISIBLE
  // (el enlace "Pseudocódigo" dejaría de estar resaltado mientras se lee la
  // guía) y un refactor no es el sitio para introducirlo.
  if (normalizado.endsWith('/guia-pseudocodigo.html')) return 'pseudocodigo';

  // Mismo criterio para la guía de diagramas (2026-08-05): mientras el alumno
  // la lee sigue viendo resaltado a qué bloque pertenece lo que está
  // aprendiendo.
  if (normalizado.endsWith('/guia-diagramas.html')) return 'diagramas';

  // Y para la guía de Python: mismo criterio que sus dos hermanas.
  if (normalizado.endsWith('/guia-python.html')) return 'python';

  // Cualquier otra ruta: ningún enlace activo. Decisión conservadora -- mejor
  // ningún `aria-current` que uno incorrecto en una página aún no contemplada.
  return null;
}

/**
 * Resuelve la sesión consultando `state/session-store.js`
 * (criterio de aceptación: nunca `fetch`/`services/**` directo desde una
 * página de shell tan temprana). Trata cualquier fallo al comprobar la
 * sesión (red/timeout/5xx, ver docstring de `cargarSesionActual`) igual que
 * "no autenticado": mismo criterio conservador que ya aplica
 * `diagram-editor-page.js#arrancarPaginaEditor` ante el mismo tipo de
 * error -- ante la duda, mostrar la barra como si no hubiese sesión
 * (enlaces de login/registro) es la opción más segura, nunca al revés (no
 * hay ningún dato protegido que ocultar mostrando login de más, mientras que
 * "autenticada" de más sí podría ocultar indebidamente esos enlaces).
 *
 * El tema claro/oscuro de la cuenta ya no se aplica (Fase 2c del rediseño:
 * cada estilo decide su modo); solo se usa una vez para migrar a quien lo
 * había elegido claro (`migrarDesdeCuenta`, en `montarShellNav`).
 * @returns {Promise<{ sesion: 'autenticada' | 'anonima', usuario: import('../services/auth-client.js').UsuarioPublico | null }>}
 */
async function resolverSesion() {
  try {
    const usuario = await cargarSesionActual();
    if (usuario) return { sesion: 'autenticada', usuario };
    return { sesion: 'anonima', usuario: null };
  } catch {
    return { sesion: 'anonima', usuario: null };
  }
}

/** @type {ReturnType<typeof crearNavegacionSuperior> | null} */
let instanciaActual = null;

/** @type {ReturnType<typeof construirMenuSesionNav> | null} */
let menuSesionActual = null;

/**
 * Monta la barra de navegación superior dentro de `contenedor` (por defecto,
 * el anclaje `#shell-nav-root`, que reservan las nueve páginas con barra). Si ya
 * había una instancia montada sin desmontar, se desmonta primero (mismo
 * criterio que el resto de páginas de este proyecto: nunca quedan dos
 * composiciones vivas a la vez ni landmarks duplicados por un doble montaje).
 *
 * Devuelve `null` sin lanzar si el anclaje no existe en el documento (mismo
 * criterio que `diagram-editor-page.js#montarPaginaEditor`/
 * `login-page.js#montarPaginaLogin`: un fallo de anclaje no es un error de
 * programación de esta página).
 * @param {HTMLElement} [contenedor]
 * @param {{ pathname?: string, sesion?: 'autenticada' | 'anonima', usuario?: { nombreVisible: string, rol: string, preferenciaTema?: string } | null, redirigirALogin?: () => void }} [opciones]
 *   `pathname` inyectable en tests (por defecto `window.location.pathname`,
 *   nunca se lee `window.location` fuera de este punto). `sesion`/`usuario`
 *   inyectables en tests para no depender de `cargarSesionActual`/red real;
 *   en producción se omiten y esta función los resuelve sola vía
 *   `resolverSesion()`. Con sesión
 *   autenticada y `usuario` disponible, monta la pastilla de usuario + botón
 *   "Cerrar sesión" (`components/session-menu.js`) en el `slotSesion` del
 *   top-nav, y el enlace 'Administración' cuando `usuario.rol === 'admin'`.
 *   `redirigirALogin` inyectable en tests (jsdom no navega el documento).
 * @returns {Promise<ReturnType<typeof crearNavegacionSuperior> | null>}
 */
export async function montarShellNav(contenedor, opciones = {}) {
  const raiz = contenedor ?? (typeof document === 'undefined' ? null : document.getElementById(ID_ANCLAJE));

  if (!raiz) return null;

  const pathname = opciones.pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const seccionActiva = detectarSeccionActiva(pathname);

  let sesion = opciones.sesion;
  let usuario = opciones.usuario;
  if (sesion === undefined || usuario === undefined) {
    const resuelto = await resolverSesion();
    sesion = sesion ?? resuelto.sesion;
    usuario = usuario ?? resuelto.usuario;
  }
  const sesionResuelta = sesion;
  const usuarioResuelto = usuario ?? null;

  if (instanciaActual) {
    desmontarShellNav();
  }

  // El selector de estilo vive en el perfil (DEC-108); aquí solo se aplica
  // el guardado. Y, con la cuenta ya conocida, la migración tiene su segunda
  // oportunidad: quien eligió el tema claro y entra desde un ordenador donde
  // todavía no ha elegido estilo pasa a Cuaderno (ver `state/visual-style.js`).
  aplicarEstiloGuardado();
  migrarDesdeCuenta(usuarioResuelto?.preferenciaTema);


  // Pastilla de usuario + botón "Cerrar sesión" (TSK sesión-en-nav): solo con
  // sesión autenticada y `usuario` disponible. El componente `session-menu.js`
  // es PURO -- la orquestación del logout (llamar a `cerrarSesion()` + redirigir)
  // vive aquí, en la página, no en el componente. El enlace 'Administración' se
  // muestra únicamente cuando el rol es admin (segunda barrera real en backend,
  // `require_roles(ADMIN)`: este enlace es solo comodidad de UI).
  const redirigir = opciones.redirigirALogin ?? redirigirALogin;
  let menuSesion = null;
  let mostrarAdmin = false;
  if (sesionResuelta === 'autenticada' && usuarioResuelto) {
    menuSesion = construirMenuSesionNav(usuarioResuelto, redirigir);
    mostrarAdmin = usuarioResuelto.rol === 'admin';
  }

  const instancia = crearNavegacionSuperior({
    seccionActiva,
    sesion: sesionResuelta,
    slotSesion: menuSesion?.elemento,
    mostrarAdmin,
  });
  raiz.replaceChildren(instancia.elemento);
  instanciaActual = instancia;
  menuSesionActual = menuSesion;
  mostrarSeccionActual(instancia.elemento);
  return instancia;
}

/**
 * Rediseño, Fase 10: por debajo de 80 rem las secciones van en una tira que
 * se desliza a lo ancho (`top-nav.css`); la actual se deja a la vista. Se
 * mueve solo la tira, nunca la página: `scrollIntoView` subiría la página
 * entera si se había abierto por un `#apartado`.
 * @param {HTMLElement} barra
 */
function mostrarSeccionActual(barra) {
  const lista = barra.querySelector('.top-nav-lista');
  const actual = barra.querySelector('[aria-current="page"]');
  if (!(lista instanceof globalThis.HTMLElement) || !(actual instanceof globalThis.HTMLElement)) {
    return;
  }
  if (lista.scrollWidth <= lista.clientWidth) return;
  lista.scrollLeft = actual.offsetLeft - lista.offsetLeft - (lista.clientWidth - actual.offsetWidth) / 2;
}

/**
 * Desmonta la barra actual (si hay una montada) y la pastilla de sesión.
 * Segura de llamar sin nada montado (no hace nada).
 */
export function desmontarShellNav() {
  if (!instanciaActual) return;
  const instancia = instanciaActual;
  const menuSesion = menuSesionActual;
  instanciaActual = null;
  menuSesionActual = null;
  instancia.destruir();
  menuSesion?.destruir();
}

// --------------------------------------------------------------------------
// Auto-montaje cuando este módulo se carga como
// `<script type="module" src="/src/pages/shell-nav.js">` desde cualquiera de
// las nueve páginas con barra (nunca JS inline en el HTML, docs/03-frontend.md
// §6.1). Guardado detrás de las mismas dos comprobaciones que el resto de
// páginas de este proyecto (`diagram-editor-page.js`, `login-page.js`):
//   1. `import.meta.env.MODE !== 'test'`: Vitest fija `MODE` a `'test'` por
//      defecto (`frontend/vite.config.js`), así que este bloque nunca se
//      ejecuta bajo `vitest run` -- los tests llaman a `montarShellNav`
//      explícitamente con su propio contenedor/opciones.
//   2. `montarShellNav()` sin argumento no hace nada si no encuentra
//      `#shell-nav-root` en el documento -- segunda red de seguridad.
if (typeof document !== 'undefined' && import.meta.env?.MODE !== 'test') {
  const iniciar = () => {
    montarShellNav();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar, { once: true });
  } else {
    iniciar();
  }
}
